import { METRIC_VALUES_TO_LABELS } from 'constants/metricValuesToLabels';
import { Formula, MetricDefinition } from '../types';

export const lastUpdated = 'March 22, 2022';

const mediums = ['Email', 'SMS', 'InApp', 'WebPush', 'Push', 'Other'];

const metricFormulasByMedium: Record<string, Record<string, Formula>> = {
  Email: {
    TotalEmailSendSkip: {},
    TotalEmailHoldOut: {},
    TotalEmailComplaint: {},
    TotalHostedUnsubscribeClick: {},
    TotalEmailSend: {},
    TotalEmailBounce: {},
    TotalEmailOpen: {},
    TotalEmailClick: {},
    TotalEmailOpensFiltered: {},
    TotalEmailOpensBotFiltered: {},
    TotalEmailDelivered: {
      numerator: { operator: 'Minus', first: 'TotalEmailSend', second: 'UniqueEmailBounce' },
      denominator: '',
    },
    UniqueEmailSend: {},
    UniqueEmailBounce: {},
    UniqueEmailOpen: {},
    UniqueEmailClick: {},
    UniqueEmailOpensFiltered: {},
    UniqueEmailOpensBotFiltered: {},
    UniqueEmailDelivered: {
      numerator: { operator: 'Minus', first: 'UniqueEmailSend', second: 'UniqueEmailBounce' },
      denominator: '',
    },
    UniqueHostedUnsubscribeClick: {},
    UniqueEmailOpenOrClick: {},
    EmailDeliveryRate: {
      numerator: { operator: 'Minus', first: 'TotalEmailSend', second: 'UniqueEmailBounce' },
      denominator: 'TotalEmailSend',
    },
    EmailBounceRate: { numerator: 'UniqueEmailBounce', denominator: 'TotalEmailSend' },
    EmailOpenRate: {
      numerator: 'UniqueEmailOpenOrClick',
      denominator: { operator: 'Minus', first: 'TotalEmailSend', second: 'UniqueEmailBounce' },
    },
    EmailClickRate: {
      numerator: 'UniqueEmailClick',
      denominator: { operator: 'Minus', first: 'TotalEmailSend', second: 'UniqueEmailBounce' },
    },
    EmailClickPerOpenRate: { numerator: 'UniqueEmailClick', denominator: 'UniqueEmailOpenOrClick' },
    TotalEmailComplaintRate: {
      numerator: 'TotalEmailComplaint',
      denominator: { operator: 'Minus', first: 'TotalEmailSend', second: 'UniqueEmailBounce' },
    },
    ConversionPerEmailDeliveredRate: {
      numerator: 'TotalCustomConversion',
      denominator: { operator: 'Minus', first: 'UniqueEmailSend', second: 'UniqueEmailBounce' },
    },
    ConversionPerEmailHoldOutRate: {
      numerator: 'TotalCustomConversion',
      denominator: 'TotalEmailHoldOut',
    },
    PurchasePerEmailSendMille: {
      numerator: { operator: 'Times', first: 'TotalPurchase', second: '1000' },
      denominator: 'TotalEmailDelivered',
    },
    RevenuePerEmailSendMille: {
      numerator: { operator: 'Times', first: 'Revenue', second: '1000' },
      denominator: 'TotalEmailDelivered',
    },
  },
  SMS: {
    TotalSMSSend: {},
    TotalSMSSendSkip: {},
    TotalSMSHoldOut: {},
    TotalSMSBounce: {},
    TotalSMSInbound: {},
    TotalSMSClick: {},
    TotalSMSDelivered: {
      numerator: { operator: 'Minus', first: 'TotalSMSSend', second: 'TotalSMSBounce' },
      denominator: '',
    },
    UniqueSMSSend: {},
    UniqueSMSBounce: {},
    UniqueSMSInbound: {},
    UniqueSMSClick: {},
    UniqueSMSDelivered: {
      numerator: { operator: 'Minus', first: 'UniqueSMSSend', second: 'UniqueSMSBounce' },
      denominator: '',
    },
    SMSBounceRate: { numerator: 'TotalSMSBounce', denominator: 'TotalSMSSend' },
    SMSDeliveryRate: { numerator: 'TotalSMSDelivered', denominator: 'TotalSMSSend' },
    SMSClickRate: { numerator: 'UniqueSMSClick', denominator: 'TotalSMSDelivered' },
    ConversionPerSMSSendRate: { numerator: 'TotalCustomConversion', denominator: 'UniqueSMSSend' },
    ConversionPerSMSDeliveredRate: {
      numerator: 'TotalCustomConversion',
      denominator: 'UniqueSMSDelivered',
    },
    ConversionPerSMSHoldOutRate: {
      numerator: 'TotalCustomConversion',
      denominator: 'TotalSMSHoldOut',
    },
    PurchasePerSMSSendMille: {
      numerator: { operator: 'Times', first: 'TotalPurchase', second: '1000' },
      denominator: 'TotalSMSDelivered',
    },
    RevenuePerSMSSendMille: {
      numerator: { operator: 'Times', first: 'Revenue', second: '1000' },
      denominator: 'TotalSMSDelivered',
    },
  },
  Push: {
    TotalPushSend: {},
    TotalPushSendSkip: {},
    TotalPushHoldOut: {},
    TotalPushOpen: {},
    TotalPushBounce: {},
    TotalPushDelivered: {
      numerator: { operator: 'Minus', first: 'TotalPushSend', second: 'TotalPushBounce' },
      denominator: '',
    },
    TotalPushUninstall: {},
    UniquePushSend: {},
    UniquePushOpen: {},
    UniquePushBounce: {},
    UniquePushDelivered: {
      numerator: { operator: 'Minus', first: 'UniquePushSend', second: 'UniquePushBounce' },
      denominator: '',
    },
    PushBounceRate: { numerator: 'TotalPushBounce', denominator: 'TotalPushSend' },
    PushDeliveryRate: { numerator: 'TotalPushDelivered', denominator: 'TotalPushSend' },
    PushOpenRate: { numerator: 'UniquePushOpen', denominator: 'TotalPushDelivered' },
    ConversionPerPushDeliveredRate: {
      numerator: 'TotalCustomConversion',
      denominator: 'UniquePushDelivered',
    },
    ConversionPerPushHoldOutRate: {
      numerator: 'TotalCustomConversion',
      denominator: 'TotalPushHoldOut',
    },
    PurchasePerPushSendMille: {
      numerator: { operator: 'Times', first: 'TotalPurchase', second: '1000' },
      denominator: 'TotalPushDelivered',
    },
    RevenuePerPushSendMille: {
      numerator: { operator: 'Times', first: 'Revenue', second: '1000' },
      denominator: 'TotalPushDelivered',
    },
  },
  WebPush: {
    TotalWebPushSend: {},
    TotalWebPushSendSkip: {},
    TotalWebPushHoldOut: {},
    TotalWebPushClick: {},
    UniqueWebPushSend: {},
    UniqueWebPushClick: {},
    ConversionPerWebPushSendRate: {
      numerator: 'TotalCustomConversion',
      denominator: 'UniqueWebPushSend',
    },
    ConversionPerWebPushHoldOutRate: {
      numerator: 'TotalCustomConversion',
      denominator: 'TotalWebPushHoldOut',
    },
  },
  InApp: {
    TotalInAppSend: {},
    TotalInAppSendSkip: {},
    TotalInAppHoldOut: {},
    TotalInAppOpen: {},
    TotalInAppDelivery: {},
    TotalInAppClick: {},
    TotalInAppClose: {},
    TotalInAppDelete: {},
    TotalInboxImpression: {},
    UniqueInAppSend: {},
    UniqueInAppOpen: {},
    UniqueInAppDelivered: {},
    UniqueInAppClick: {},
    InAppClickRate: { numerator: 'TotalInAppClick', denominator: 'TotalInAppOpen' },
    ConversionPerInAppOpenRate: {
      numerator: 'TotalCustomConversion',
      denominator: 'UniqueInAppOpen',
    },
    ConversionPerInAppHoldOutRate: {
      numerator: 'TotalCustomConversion',
      denominator: 'TotalInAppHoldOut',
    },
  },
  Other: {
    TotalPurchase: {},
    UniquePurchase: {},
    Revenue: {},
    AverageOrderValue: { numerator: 'Revenue', denominator: 'TotalPurchase' },
    TotalCustomConversion: {},
    UniqueCustomConversion: {},
  },
};

const metricFormulas: Record<string, Formula> = mediums.reduce(
  (acc, medium) => ({ ...acc, ...metricFormulasByMedium[medium] }),
  {},
);

type MetricDescription = {
  description: string;
  subtext: string[];
};

const metricDescriptions: Record<string, MetricDescription> = {
  'Average Order Value': { description: 'Revenue / Total Purchases', subtext: [] },
  'Clicks / Opens': {
    description: 'Unique Email Clicks / Unique Email Opens or Clicks',
    subtext: [],
  },
  'Complaint Rate': { description: 'Total Complaints / Total Emails Delivered', subtext: [] },
  Conversions: {
    description: 'The number of conversions that resulted from the campaign.',
    subtext: [],
  },
  'Conversions / Email Hold Outs': {
    description:
      'The total number of conversions for an email campaign divided by the number of holdouts for that campaign.',
    subtext: [],
  },
  'Conversions / In-app Hold Outs': {
    description:
      'The total number of conversions for an in-app message campaign divided by the number of holdouts for that campaign.',
    subtext: [],
  },
  'Conversions / Push Hold Outs': {
    description:
      'The total number of conversions for a push notification campaign divided by the number of holdouts for that campaign.',
    subtext: [],
  },
  'Conversions / SMS Hold Outs': {
    description:
      'The total number of conversions for an SMS campaign divided by the number of holdouts for that campaign.',
    subtext: [],
  },
  'Conversions / Unique Email Sends': {
    description:
      'The total number of conversions for a campaign divided by the number of unique emails it sent',
    subtext: [],
  },
  'Conversions / Unique Emails Delivered': {
    description:
      'The total number of conversions for a campaign divided by the number of unique emails delivered',
    subtext: [],
  },
  'Conversions / Unique In-App Opens': {
    description:
      'The total number of conversions for a campaign divided by the number of unique in-app opens',
    subtext: [],
  },
  'Conversions / Unique In-App Sends': {
    description:
      'The total number of conversions for a campaign divided by the number of unique in-app messages it sent',
    subtext: [],
  },
  'Conversions / Unique Push Sends': {
    description:
      'The total number of conversions for a campaign divided by the number of unique push notifications it sent',
    subtext: [],
  },
  'Conversions / Unique SMS Sends': {
    description:
      'The total number of conversions for a campaign divided by the number of unique SMS messages it sent',
    subtext: [],
  },
  'Conversions / Unique Web Push Sends': {
    description:
      'The total number of conversions for a campaign divided by the number of unique web push messages it sent',
    subtext: [],
  },
  'Conversions / Web Push Hold Outs': {
    description:
      'The total number of conversions for a web push campaign divided by the number of holdouts for that campaign.',
    subtext: [],
  },
  'Email Bounce Rate': {
    description: 'Total Emails Bounced / Total Emails Delivered',
    subtext: [],
  },
  'Email Click Rate': { description: 'Unique Email Clicks / Total Emails Delivered', subtext: [] },
  'Email Open Rate': {
    description: 'Unique Email Opens or Clicks / Total Emails Delivered',
    subtext: [],
  },
  'Email Unsubscribe Rate': {
    description: 'Total Unsubscribes / Total Emails Delivered',
    subtext: [],
  },
  Revenue: {
    description:
      'The amount of revenue that resulted from the campaign. For more information, read Tracking Purchases and Revenue.',
    subtext: [],
  },
  'Revenue / M': { description: 'Revenue per Mille (thousand emails sent)', subtext: [] },
  'Sum of Custom Conversions': { description: 'The sum of custom conversion totals.', subtext: [] },
  'Total Complaints': {
    description: 'The number of individuals who reported your email as spam.',
    subtext: [],
  },
  'Total Email Holdout': {
    description:
      'For a given campaign, the number of email sends that were skipped because of an experiment holdout group. On the profiles of skipped users, Iterable creates emailSendSkip events with reason set to ExperimentHoldOut.',
    subtext: [],
  },
  'Total Email Opens': {
    description:
      'The total number of emails opened for a campaign, which can include multiple opens per person (for example, for triggered or recurring campaigns). This number can include pre-fetched and proxied email opens, which may inflate open rates.',
    subtext: [],
  },
  'Total Email Opens (filtered)': {
    description:
      'This metric is more accurate than Total Email Opens because it excludes pre-fetched and proxied opens, but it may narrow the scope of your reporting.',
    subtext: [],
  },
  'Total Email Sends': {
    description:
      'The total number of emails sent for a campaign. For triggered campaigns, this number can include multiple sends to the same email address. If this number is less than the list size, Iterable may have skipped sending some messages for one of the reasons described in Send Skips.',
    subtext: [],
  },
  'Total Email Send Skips': {
    description:
      'The total number of email messages that Iterable did not (or could not) send. For information about the reasons Iterable might skip sending an email, see Send Skips.',
    subtext: [],
  },
  'Total Emails Bounced': {
    description:
      'The number of hard or soft-bounced email addresses. For more information, read Bounce Definitions, Reporting, and Segmentation.',
    subtext: [],
  },
  'Total Emails Clicked': {
    description: 'The total number of clicks, which can include multiple clicks per person.',
    subtext: [],
  },
  'Total Emails Delivered': {
    description: 'The total number of emails delivered successfully (sends minus bounces).',
    subtext: [],
  },
  'Total Hosted Unsubscribed Clicks': {
    description:
      'The total number of hosted unsubscribe link clicks, which can include multiple clicks per person.',
    subtext: [],
  },
  'Total In-app Holdout': {
    description:
      'For a given campaign, the number of in-app message sends that were skipped because of an experiment holdout group. On the profiles of skipped users, Iterable creates inAppSendSkip events on which reason is set ExperimentHoldOut.',
    subtext: [],
  },
  'Total of Custom Conversions': { description: 'Number of custom conversions', subtext: [] },
  'Total Purchases': {
    description:
      'The number of purchases that resulted from the campaign. For more information, read Tracking Purchases and Revenue.',
    subtext: [],
  },
  'Total Push Holdout': {
    description:
      'For a given campaign, the number of push notification sends that were skipped because of an experiment holdout group. On the profiles of skipped users, Iterable creates pushSendSkip events on which reason is set ExperimentHoldOut.',
    subtext: [],
  },
  'Total SMS Holdout': {
    description:
      'For a given campaign, the number of SMS sends that were skipped because of an experiment holdout group. On the profiles of skipped users, Iterable creates smsSendSkip events on which reason is set ExperimentHoldOut.',
    subtext: [],
  },
  'Total Unsubscribes': {
    description:
      'The total number of unsubscribes that resulted from the campaign. A person can unsubscribe more than once if they resubscribe and then unsubscribe, again, from the same email campaign.',
    subtext: [],
  },
  'Total WebPush Holdout': {
    description:
      'For a given campaign, the number of web push notification sends that were skipped because of an experiment holdout group. On the profiles of skipped users, Iterable creates webPushSendSkip events on which reason is set ExperimentHoldOut.',
    subtext: [],
  },
  'Unique Email Clicks': {
    description:
      'The number of individual users who clicked your email. Does not include clicks on unsubscribe URLs.',
    subtext: [],
  },
  'Unique Email Opens': {
    description:
      'The number of unique users who opened an email associated with this campaign. This number doesn’t include multiple opens from the same email address or opens from users who have images turned off in their email client. It can include pre-fetched and proxied email opens, which may inflate open rates.',
    subtext: [],
  },
  'Unique Email Opens (filtered)': {
    description:
      'This metric is more accurate than Unique Email Opens because it excludes pre-fetched and proxied opens, but it may narrow the scope of your reporting.',
    subtext: [],
  },
  'Unique Email Opens or Clicks': {
    description:
      'The number of unique individual users who opened your email. If a user has images turned off but still clicks on the email, Iterable registers an open.',
    subtext: [
      'Here are four scenarios and the number of opens tracked:',
      "A person opens the email with images disabled and doesn't click on anything: 0",
      'A person opens the email with images disabled and clicks on a link: 1',
      "A person opens the email with images on and doesn't click on anything: 1",
      'A person opens the email with images on and clicks on something: 1',
    ],
  },
  'Unique Email Sends': {
    description:
      'An approximation, not an exact count, of the number of emails sent to unique email addresses. This number counts multiple sends to the same email address as a single send.',
    subtext: [
      'If this number is less than the list size, Iterable may have skipped sending some messages for one of the reasons described in Send Skips.',
    ],
  },
  'Unique Emails Bounced': {
    description:
      'The number of email bounces resulting from this campaign. Multiple bounces from the same email address are counted only once.',
    subtext: [],
  },
  'Unique Emails Delivered': {
    description:
      'The number of emails successfully delivered to unique email addresses. Multiple deliveries to the same email address are counted only once.',
    subtext: [],
  },
  'Unique Hosted Unsubscribed Clicks': {
    description: 'The number of individual users who clicked your hosted unsubscribe link.',
    subtext: [],
  },
  'Unique Purchases': {
    description:
      'The number of users who made a purchase from the campaign. Users who make more than one purchase are counted only once.',
    subtext: [],
  },
  'Unique Unsubscribes': {
    description:
      'The number of users who unsubscribed from the email that was sent with this campaign. If a user subscribes and unsubscribes more than once from this email, their unsubscribes are counted only once.',
    subtext: [],
  },
  'Conversions / Unique SMS delivered': {
    description:
      '(Total number of custom conversion events attributed to the campaign) / (Unique SMS delivered)',
    subtext: [],
  },
  'Conversions / Unique SMS sends': {
    description:
      '(Total number of custom conversion events attributed to the campaign) / (Unique SMS sent)',
    subtext: [],
  },
  'Purchases / M (SMS)': {
    description: '(Total purchases) * 1000 / (Total SMS delivered)',
    subtext: [],
  },
  'Revenue / M (SMS)': { description: '(Revenue * 1000) / (Total SMS delivered)', subtext: [] },
  'SMS bounce rate': { description: '(Total SMS bounced) / (Total SMS sent)', subtext: [] },
  'SMS click rate': { description: '(Unique SMS clicks) / (Total SMS delivered)', subtext: [] },
  'SMS delivery rate': { description: '(Total SMS delivered) / (Total SMS sent)', subtext: [] },
  'Total SMS bounced': {
    description:
      'The total number of SMS messages that Iterable successfully passed to Twilio, but that Twilio could not send.',
    subtext: [
      'Bounces can happen for various reasons: invalid phone numbers, a malformed image URL, because the contact has previously unsubscribed, etc.',
      'Notes:',
      'When an SMS bounces, contacts are not automatically unsubscribed.',
      "To see the error code of associated with an SMS bounce, find the SMS bounce event on the contact's Iterable profile and locate the errorCode field. To understand what the code means, use Twilio's documentation to look it up.",
    ],
  },
  'Total SMS Clicks': {
    description: 'The total number of SMS click events associated with the campaign.',
    subtext: [],
  },
  'Total SMS delivered': { description: '(Total SMS sent) - (Total SMS bounced)', subtext: [] },
  'Total SMS holdout': {
    description:
      'The total number of SMS messages that Iterable did not attempt to send to Twilio, because of a holdout group experiment.',
    subtext: [],
  },
  'Total SMS send skips': {
    description:
      'The total number of SMS messages that Iterable did not (or could not) send to Twilio, for any reason.',
    subtext: ['If you are seeing SMS send skips, talk to your customer success manager.'],
  },
  'Total SMS sent': {
    description:
      'The total number of SMS messages sent for the campaign. More specifically, the number of messages passed to Twilio (the SMS carrier). For triggered campaigns, this can include multiple sends to the same number.',
    subtext: [],
  },
  'Unique SMS bounced': {
    description:
      'The total number of SMS bounce events, counting at most one bounce per Iterable contact.',
    subtext: [],
  },
  'Unique SMS clicks': {
    description:
      'The total number of SMS click events captured for the campaign, counting at most one click per Iterable contact.',
    subtext: [],
  },
  'Unique SMS delivered': { description: '(Unique SMS sent) - (Unique SMS bounced)', subtext: [] },
  'Unique SMS sent': {
    description:
      'An approximation, not an exact count, of the number of SMS messages sent for the campaign.',
    subtext: [],
  },
};

export const metricsWithDefinitions = new Set<string>([]);

// Used to successfully index into the metric definitions (regardless of the metric's casing)
const getMetricDescription = (metric: string) => {
  const key = Object.keys(metricDescriptions).find(
    (metricLabel) => metricLabel.toLowerCase() === METRIC_VALUES_TO_LABELS[metric].toLowerCase(),
  );
  return key ? metricDescriptions[key] : {};
};

export const metricDefinitionsByMedium: Record<string, Record<string, MetricDefinition>> = {};
mediums.forEach((medium) => {
  metricDefinitionsByMedium[medium] = {};
  Object.keys(metricFormulasByMedium[medium]).forEach((metric) => {
    const label = METRIC_VALUES_TO_LABELS[metric] || metric;
    metricsWithDefinitions.add(label.toLowerCase());
    metricDefinitionsByMedium[medium][metric.toLowerCase()] = {
      label,
      ...metricFormulas[metric],
      ...getMetricDescription(metric),
    };
  });
});

export const metricDefinitions: Record<string, MetricDefinition> = mediums.reduce(
  (acc, medium) => ({ ...acc, ...metricDefinitionsByMedium[medium] }),
  {},
);
